import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/shadcn";

interface DatePeriodSelectorProps {
  onDateChange?: (startDate: Date | null, endDate: Date | null) => void;
  className?: string;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  placeholder?: string;
  initialStartDate?: Date;
  initialEndDate?: Date;
}

export function DatePeriodSelector({
  onDateChange,
  className,
  minDate,
  maxDate,
  disabled = false,
  placeholder = "Select date range",
  initialStartDate,
  initialEndDate
}: DatePeriodSelectorProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [startDate, setStartDate] = useState<Date | null>(initialStartDate || null);
  const [endDate, setEndDate] = useState<Date | null>(initialEndDate || null);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
  ];

  const dayNames = ["S", "M", "T", "W", "T", "F", "S"];

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const getNextMonth = (date: Date) => {
    const nextMonth = new Date(date);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    return nextMonth;
  };

  const navigateMonth = (direction: "prev" | "next") => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev);
      newMonth.setMonth(newMonth.getMonth() + (direction === "next" ? 1 : -1));
      return newMonth;
    });
  };

  const handleDateClick = (date: Date) => {
    if (!startDate || (startDate && endDate)) {
      // Start new selection
      setStartDate(date);
      setEndDate(null);
    } else if (date < startDate) {
      // If clicked date is before start date, make it the new start date
      setStartDate(date);
      setEndDate(null);
    } else {
      // Set end date
      setEndDate(date);
    }
  };

  const isDateInRange = (date: Date) => {
    if (!startDate) return false;
    if (startDate && endDate) {
      return date >= startDate && date <= endDate;
    }
    if (startDate && hoverDate && hoverDate >= startDate) {
      return date >= startDate && date <= hoverDate;
    }
    return false;
  };

  const isDateRangeStart = (date: Date) => startDate && date.getTime() === startDate.getTime();

  const isDateRangeEnd = (date: Date) => {
    if (endDate && date.getTime() === endDate.getTime()) return true;
    if (
      !endDate &&
      hoverDate &&
      startDate &&
      hoverDate >= startDate &&
      date.getTime() === hoverDate.getTime()
    )
      return true;
    return false;
  };

  const formatDateRange = () => {
    if (!startDate) return placeholder;
    if (!endDate) return `${startDate.toLocaleDateString()} - Select end date`;
    return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
  };

  const clearDates = () => {
    setStartDate(null);
    setEndDate(null);
    setHoverDate(null);
  };

  useEffect(() => {
    onDateChange?.(startDate, endDate);
  }, [startDate, endDate, onDateChange]);

  const renderCalendar = (monthDate: Date) => {
    const days = getDaysInMonth(monthDate);

    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground">
            {monthNames[monthDate.getMonth()]} {monthDate.getFullYear()}
          </h3>
          {monthDate.getTime() === currentMonth.getTime() && (
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigateMonth("prev")}
                className="h-8 w-8 p-0 hover:bg-muted"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigateMonth("next")}
                className="h-8 w-8 p-0 hover:bg-muted"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNames.map((day) => (
            <div
              key={day}
              className="h-10 flex items-center justify-center text-sm font-medium text-muted-foreground"
            >
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {days.map((day, index) => (
            <div key={index} className="h-10 flex items-center justify-center">
              {day && (
                <button
                  onClick={() => handleDateClick(day)}
                  onMouseEnter={() => setHoverDate(day)}
                  onMouseLeave={() => setHoverDate(null)}
                  disabled={disabled || (minDate && day < minDate) || (maxDate && day > maxDate)}
                  className={cn(
                    "h-10 w-10 rounded-full text-sm font-medium transition-all duration-200 hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed",
                    isDateRangeStart(day) &&
                      "bg-primary text-primary-foreground hover:bg-primary/90",
                    isDateRangeEnd(day) && "bg-primary text-primary-foreground hover:bg-primary/90",
                    isDateInRange(day) &&
                      !isDateRangeStart(day) &&
                      !isDateRangeEnd(day) &&
                      "bg-primary/10",
                    day.getTime() === new Date().setHours(0, 0, 0, 0) &&
                      "ring-2 ring-primary ring-offset-2"
                  )}
                >
                  {day.getDate()}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={cn("relative", className)}>
      <Button
        variant="outline"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="w-full justify-between h-14 px-4 text-left font-normal border-2 hover:border-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <div className="flex items-center gap-3">
          <Calendar className="h-5 w-5 text-muted-foreground" />
          <div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">
              Start Date — End Date
            </div>
            <div className="text-sm font-medium">{formatDateRange()}</div>
          </div>
        </div>
      </Button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 bg-card border border-border rounded-xl shadow-2xl z-50 w-full min-w-[600px] max-w-[800px]">
          <div className="flex flex-col lg:flex-row">
            <div className="flex-1">{renderCalendar(currentMonth)}</div>
            <div className="border-t lg:border-t-0 lg:border-l border-border flex-1">
              {renderCalendar(getNextMonth(currentMonth))}
            </div>
          </div>

          <div className="flex items-center justify-between p-6 border-t border-border">
            <Button
              variant="ghost"
              onClick={clearDates}
              className="text-sm font-medium underline hover:no-underline"
            >
              Clear dates
            </Button>
            <Button
              onClick={() => setIsOpen(false)}
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-6"
            >
              Close
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
